import { FC } from "react";
import { Inventory } from "@mui/icons-material";
import { toBytes, concat, pad, toHex } from "viem";

import { useMetamask, useSystemContract, useWriteContractWithSimulation } from "@dzambalaorg/react-hooks-eth";
import { useUser } from "@dzambalaorg/provider-user";
import { ListAction, ListActionVariant } from "@framework/styled";
import {
  type AddressType,
  type IClaim,
  type IContract,
  type IUser,
  SystemModuleType,
  TokenType,
} from "@framework/types";
import VestingDeployABI from "@framework/abis/json/VestingFactoryFacet/deployVesting.json";

import { sorter } from "../../../../../utils/sorter";

export interface IVestingDeployButtonProps {
  claim: IClaim;
  className?: string;
  disabled?: boolean;
  variant?: ListActionVariant;
}

export const VestingDeployButton: FC<IVestingDeployButtonProps> = props => {
  const { claim, className, disabled, variant } = props;
  const { profile } = useUser<IUser>();
  const { writeContractAsync } = useWriteContractWithSimulation();

  const encodedExternalId = concat([pad(toHex(profile.id), { size: 3 }), pad(toHex(claim.id), { size: 4 })]);

  const metaFnWithContract = useSystemContract<IContract, SystemModuleType>(
    async (claim: IClaim, systemContract: IContract) => {
      return writeContractAsync({
        address: systemContract.address as AddressType,
        abi: VestingDeployABI,
        functionName: "deployVesting",
        args: [
          {
            nonce: toBytes(claim.nonce, { size: 32 }),
            bytecode: claim.parameters.bytecode,
            externalId: encodedExternalId,
          },
          {
            owner: claim.parameters.owner,
            startTimestamp: Math.ceil(new Date(claim.parameters.startTimestamp).getTime() / 1000),
            cliffInMonth: claim.parameters.cliffInMonth,
            monthlyRelease: claim.parameters.monthlyRelease,
          },
          claim.item?.components.sort(sorter("id")).map(component => ({
            tokenType: Object.values(TokenType).indexOf(component.tokenType),
            token: component.contract?.address,
            tokenId: (component.templateId || 0).toString(), // suppression types check with 0
            amount: component.amount,
          })),
          claim.signature,
        ],
      });
    },
  );

  const { execute: metaFn } = useMetamask((values: IClaim) => {
    return metaFnWithContract(SystemModuleType.CONTRACT_MANAGER, values);
  });

  const handleClick = () => {
    return metaFn(claim);
  };

  return (
    <ListAction
      onClick={handleClick}
      icon={Inventory}
      message="form.tips.deploy"
      className={className}
      dataTestId="VestingDeployButton"
      disabled={disabled}
      variant={variant}
    />
  );
};
